// Gathering nodes (decision 0058: herbs and flowers to forage and ore to mine, respawning on a timer). A node kind is
// data (what it yields, how long it takes to grow back, the pose and time the gather takes); nodes are placements of a
// kind in the world, keyed by their placement id. Their state (collected, when it grows back) is plain data on sim
// time the game keeps in its sim and its save; a node never touched has no entry and is ready. The yields roll on the
// game's seeded stream.
import type { ItemTable } from "./items";

/** One yield line: an item and how many (a whole number between min and max), with a chance to give anything. */
export interface YieldDef {
  item: string;
  min: number;
  max: number;
  /** 0..1; default 1. */
  chance?: number;
}

/** A node kind (a herb, a flower, an ore vein). */
export interface GatherNodeDef {
  id: string;
  yields: readonly YieldDef[];
  /** Sim seconds from gathered to ready again. */
  respawn: number;
  /** The gather's pose id (the game's pose table) and how long the gatherer is held in it (s). */
  pose: string;
  seconds: number;
}

/** One node's state. */
export interface GatherNodeState {
  /** Gathered and not grown back yet. */
  collected: boolean;
  /** Sim time it grows back (s). */
  respawnAt: number;
  /** Times gathered. */
  times: number;
}

/** Every touched node's state, by placement id. */
export interface GatherState {
  nodes: Record<string, GatherNodeState>;
}

/** What a gather gave. */
export interface Gathered {
  item: string;
  count: number;
}

/** Planted defects, beside what they break (tests only). */
export interface GatherFaults {
  /** Nodes never grow back: the respawn checks must go red. */
  neverRegrow: boolean;
}

/** Problems with node kinds against the item table (unknown items, bad numbers); empty when clean. */
export function checkGatherNodes(defs: readonly GatherNodeDef[], items: ItemTable): string[] {
  const out: string[] = [];
  for (const d of defs) {
    if (!(d.respawn > 0)) out.push(`node "${d.id}": respawn ${d.respawn} s`);
    if (!(d.seconds >= 0)) out.push(`node "${d.id}": gather time ${d.seconds} s`);
    if (!d.yields.length) out.push(`node "${d.id}" yields nothing`);
    for (const y of d.yields) {
      if (!items.has(y.item)) out.push(`node "${d.id}" yields a missing item "${y.item}"`);
      if (!Number.isInteger(y.min) || !Number.isInteger(y.max) || y.min < 0 || y.max < y.min) out.push(`node "${d.id}": ${y.item} ${y.min}..${y.max}`);
      if (y.chance !== undefined && !(y.chance >= 0 && y.chance <= 1)) out.push(`node "${d.id}": ${y.item} chance ${y.chance}`);
    }
  }
  return out;
}

/** Roll yield lines on a seeded stream (`random` in [0, 1)): each line's chance, then its count. Lines in order;
 *  zero counts left out. */
export function rollYields(lines: readonly YieldDef[], random: () => number): Gathered[] {
  const out: Gathered[] = [];
  for (const y of lines) {
    if (y.chance !== undefined && y.chance < 1 && random() >= y.chance) continue;
    const count = y.min + Math.floor(random() * (y.max - y.min + 1));
    if (count > 0) out.push({ item: y.item, count: Math.min(count, y.max) });
  }
  return out;
}

/** The gathering rules over node kinds. */
export class Gathering {
  /** Planted defects (tests only). */
  readonly faults: GatherFaults = { neverRegrow: false };
  private readonly kinds: Map<string, GatherNodeDef>;

  /** Throws listing every problem `checkGatherNodes` finds. */
  constructor(defs: readonly GatherNodeDef[], items: ItemTable) {
    const issues = checkGatherNodes(defs, items);
    if (issues.length) throw new Error(`gathering: ${issues.join("; ")}`);
    this.kinds = new Map(defs.map((d) => [d.id, d]));
  }

  /** A node kind. Throws on an unknown id. */
  kind(id: string): GatherNodeDef {
    const k = this.kinds.get(id);
    if (!k) throw new Error(`gathering: no node kind "${id}"`);
    return k;
  }

  /** Whether a node can be gathered now. */
  ready(s: GatherState, placement: string): boolean {
    return !s.nodes[placement]?.collected;
  }

  /** Gather a node of `kind` at sim time `now`: its yields (rolled on `random`), the node collected until
   *  `now + respawn`. Null when it isn't ready. */
  gather(s: GatherState, placement: string, kind: string, now: number, random: () => number): Gathered[] | null {
    if (!this.ready(s, placement)) return null;
    const k = this.kind(kind);
    const n = (s.nodes[placement] ??= { collected: false, respawnAt: 0, times: 0 });
    n.collected = true;
    n.respawnAt = now + k.respawn;
    n.times++;
    return rollYields(k.yields, random);
  }

  /** Grow back every node whose time has come (call each sim step); returns their placement ids, in id order. */
  step(s: GatherState, now: number): string[] {
    if (this.faults.neverRegrow) return [];
    const out: string[] = [];
    for (const id of Object.keys(s.nodes).sort()) {
      const n = s.nodes[id]!;
      if (n.collected && now >= n.respawnAt - 1e-9) {
        n.collected = false;
        out.push(id);
      }
    }
    return out;
  }
}
